import { z } from 'zod';

import type {
  NotificationKind,
  NotificationState,
} from '@/lib/api/generated/model';

import { kindsOf, TYPE_GROUP_IDS, type TypeGroup } from './typeGroups';

/** The page's status tabs (spec 147 Q4); absent from the address means All. */
export const PAGE_STATUSES = [
  'all',
  'needs_you',
  'unread',
  'archived',
] as const;
export type PageStatus = (typeof PAGE_STATUSES)[number];

export const STATUS_LABEL: Record<PageStatus, string> = {
  all: 'All',
  needs_you: 'Needs you',
  unread: 'Unread',
  archived: 'Archived',
};

/** The Date filter's choices; absent means All. */
export const DATE_RANGES = ['today', '7d', '30d', 'all', 'custom'] as const;
export type DateRange = (typeof DATE_RANGES)[number];

export const RANGE_LABEL: Record<DateRange, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  all: 'All',
  custom: 'Custom',
};

export const STATES = ['open', 'resolved'] as const;

export const STATE_LABEL: Record<NotificationState, string> = {
  open: 'Open',
  resolved: 'Resolved',
};

const LOCAL_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A `YYYY-MM-DD` that names a real day, as a local midnight; else `null`. */
function localDay(value: string): Date | null {
  const match = LOCAL_DAY.exec(value);
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number) as [
    number,
    number,
    number,
  ];
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : null;
}

/**
 * A day in the address. The router parses search values as JSON, so a value
 * that happens to be a number arrives as one: anything that is not a real
 * `YYYY-MM-DD` falls back to absent rather than failing the route.
 */
const daySearchField = z
  .preprocess(
    (value) => (typeof value === 'number' ? String(value) : value),
    z.string().refine((value) => localDay(value) !== null),
  )
  .optional()
  .catch(undefined);

/**
 * The picked types. The router writes an array as JSON, but a hand-written
 * `?type=runs` or `?type=runs,spend` is read too. Unknown ids are dropped,
 * the rest kept in menu order, and an empty pick is absent.
 */
const typeSearchField = z
  .preprocess(
    (value) => {
      if (value === undefined || value === null) return undefined;
      const list: unknown[] = Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? value.split(',')
          : [value];
      const picked = new Set(list.map((item) => String(item).trim()));
      const known = TYPE_GROUP_IDS.filter((id) => picked.has(id));
      return known.length > 0 ? known : undefined;
    },
    z.array(z.enum(TYPE_GROUP_IDS as [TypeGroup, ...TypeGroup[]])).optional(),
  )
  .catch(undefined);

/**
 * The page's whole view, in the address (spec 147 FR-002), so Back and a
 * shared link show the same notifications. Every field falls back to absent
 * on a value it does not recognise: a bad bookmark is not an error page.
 */
export const notificationsSearchSchema = z.object({
  status: z.enum(PAGE_STATUSES).optional().catch(undefined),
  workspace: z.uuid().optional().catch(undefined),
  type: typeSearchField,
  range: z.enum(DATE_RANGES).optional().catch(undefined),
  from: daySearchField,
  to: daySearchField,
  state: z.enum(STATES).optional().catch(undefined),
});

export type NotificationsSearch = z.infer<typeof notificationsSearchSchema>;

/**
 * What the service is asked for: the list's, the count's and the whole-filter
 * actions' filter alike (service spec 130), with only the fields that are set,
 * so it is a stable query key and a clean request body.
 */
export interface InboxQuery {
  unread?: true;
  archived?: true;
  group?: 'needs_you';
  workspace_id?: string;
  kind?: NotificationKind[];
  created_from?: string;
  created_to?: string;
  state?: NotificationState;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * The instants a date choice means, in the reader's local days: "Last 7 days"
 * is today and the six before it. A custom To is inclusive on the page and
 * exclusive at the service, so it becomes the next day's midnight.
 */
function dateBounds(
  search: NotificationsSearch,
  now: Date,
): { from?: Date; to?: Date } {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  switch (search.range) {
    case 'today':
      return { from: today };
    case '7d':
      return { from: addDays(today, -6) };
    case '30d':
      return { from: addDays(today, -29) };
    case 'custom': {
      let from = search.from ? localDay(search.from) : null;
      let to = search.to ? localDay(search.to) : null;
      // Picked back to front: the days between them are still what was meant.
      if (from && to && to < from) [from, to] = [to, from];
      return {
        from: from ?? undefined,
        to: to ? addDays(to, 1) : undefined,
      };
    }
    case 'all':
    case undefined:
      return {};
  }
}

export function toInboxQuery(
  search: NotificationsSearch,
  now: Date = new Date(),
): InboxQuery {
  const query: InboxQuery = {};
  if (search.status === 'unread') query.unread = true;
  if (search.status === 'archived') query.archived = true;
  if (search.status === 'needs_you') query.group = 'needs_you';
  if (search.workspace) query.workspace_id = search.workspace;
  // Every type is every kind, so it is asked for as no filter at all: the service
  // caps `kind` at one value per kind (20 since service spec 139), and asking for
  // them all would only repeat what no filter means.
  if (
    search.type &&
    search.type.length > 0 &&
    search.type.length < TYPE_GROUP_IDS.length
  )
    query.kind = kindsOf(search.type);
  const { from, to } = dateBounds(search, now);
  if (from) query.created_from = from.toISOString();
  if (to) query.created_to = to.toISOString();
  if (search.state) query.state = search.state;
  return query;
}

/** How many of the filters beside the status tabs are set: "Filters · N". */
export function activeFilterCount(search: NotificationsSearch): number {
  return [
    search.workspace,
    search.type?.length ? search.type : undefined,
    search.range && search.range !== 'all' ? search.range : undefined,
    search.state,
  ].filter((value) => value !== undefined).length;
}

/** The address with every filter but the status tab cleared. */
export function clearedFilters(
  search: NotificationsSearch,
): NotificationsSearch {
  return { status: search.status };
}
